var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../../ui/Buttons/Button";
import Input from "../../ui/Input/Input";
import Tour from "../../ui/Tour/Tour";
import CopyReportButton from "./CopyReportButton";
import StepIllustration from "./StepIllustration";
import { getUnderlyingPlayer } from "./useStreamDiagnostics";
import { canInspectCues, detectCapabilities, selectableTextTracks, trackKey } from "./playbackCapabilities";
var STEPS = [
    {
        id: "unmute",
        title: "Sound",
        instruction: "Click the speaker icon in the control bar to unmute, then raise the volume.",
        // iOS keeps `volume` read-only and the control bar renders no slider
        // there, so asking for one sends the tester looking for a control that
        // does not exist on their device.
        instructionWithoutVolumeControl: "Tap the speaker icon in the control bar to unmute. Then use the device's own volume buttons.",
        shows: "the speaker icon in the control bar, with the volume slider raised",
        confirm: "Can you hear the audio now?",
        illustration: "volume",
        unavailable: ""
    },
    {
        id: "audioLanguage",
        title: "Audio language",
        instruction: "Open the audio menu in the control bar and pick a different language.",
        shows: "the audio menu open, with the language list showing",
        confirm: "Did the spoken language actually change?",
        illustration: "audioMenu",
        unavailable: "This stream carries a single audio track."
    },
    {
        id: "subtitlesOn",
        title: "Subtitles",
        instruction: "Open the subtitles/CC menu and switch subtitles on.",
        shows: "the subtitles/CC menu open, with a language being switched on",
        confirm: "Can you see subtitles on the video?",
        illustration: "subtitlesMenu",
        unavailable: "This stream carries no subtitle or caption track."
    },
    {
        id: "subtitlesLanguage",
        title: "Subtitle language",
        instruction: "Back in the subtitles menu, switch to another language.",
        shows: "the subtitles menu open, with a second language highlighted",
        confirm: "Did the subtitle language actually change?",
        illustration: "subtitlesMenu",
        unavailable: "This stream carries a single subtitle track."
    },
    {
        id: "bandwidth",
        title: "Bandwidth",
        instruction: "Leave the stream playing and do not touch the controls while the line is measured.",
        note: "Download speed settles near the stream's own bitrate once playback is steady, so a modest ratio here is normal.",
        unavailable: "This browser plays the stream itself and reports no download figures to the page, so there is nothing to measure.",
        group: "line"
    },
    {
        id: "stability",
        title: "Stability",
        instruction: "Reading what happened to playback during the measurement.",
        // Where the bandwidth step could not run there is no measurement to read,
        // so this one holds its own window and has to ask for the same quiet.
        instructionMeasuringAlone: "Leave the stream playing and do not touch the controls while playback is watched.",
        unavailable: "",
        group: "line"
    }
];
/** Step ids in running order, with their titles, for anything rendering a summary elsewhere. */
export var WIZARD_STEPS = STEPS.map(function (step) { return ({
    id: step.id,
    title: step.title
}); });
/**
 * The single rule for turning per-step results into an overall verdict, shared
 * by the summary light and the copied report so the two cannot disagree.
 * Anything not passed is a failure whatever else happened; warnings alone are
 * amber; checks the stream does not offer count as neither, being no fault.
 */
export function wizardVerdict(results) {
    var states = WIZARD_STEPS.map(function (step) { var _a; return (_a = results[step.id]) === null || _a === void 0 ? void 0 : _a.state; });
    if (states.some(function (state) { return state === "failed" || state === "missed"; }))
        return "fail";
    if (states.some(function (state) { return state === "warn"; }))
        return "warn";
    return "pass";
}
export var VERDICT_LABEL = {
    pass: "Everything checked out",
    warn: "Works, with points to review",
    fail: "Problems found"
};
var SIMULATED_RESULTS = {
    warn: {
        unmute: { state: "done", detail: "Unmuted at 100% volume" },
        audioLanguage: { state: "unavailable", detail: "This stream carries a single audio track." },
        subtitlesOn: {
            state: "warn",
            detail: "English (en) is active but no cue appeared in 10s (empty or unreachable VTT?)"
        },
        subtitlesLanguage: { state: "unavailable", detail: "This stream carries a single subtitle track." },
        bandwidth: {
            state: "done",
            detail: "2.4 Mbps of content pulled at 27.4 Mbps (11.4x real time) over 20s. No stalls, no failed requests and no quality drops: the line carries this stream."
        },
        stability: {
            state: "done",
            detail: "Over 20s: no rebuffering, 0 rendition change(s), none downward, 0.00% frames dropped."
        }
    },
    fail: {
        unmute: { state: "done", detail: "Unmuted at 80% volume" },
        audioLanguage: { state: "done", detail: "Audio switched to English (en)" },
        subtitlesOn: { state: "done", detail: "Italiano (it) on, cues rendering" },
        subtitlesLanguage: { state: "missed", detail: "No change detected." },
        bandwidth: {
            state: "failed",
            detail: "2.4 Mbps of content pulled at 2.1 Mbps (0.9x real time) over 20s. 3 segment request(s) failed or timed out."
        },
        stability: {
            state: "warn",
            detail: "Over 20s: 2 rebuffer(s) for 3.4s, 1 step(s) down in quality, 0.80% frames dropped."
        }
    }
};
/** How long a user-driven step waits for its event before it is reported as not detected. */
var STEP_TIMEOUT_MS = 25000;
/** How often the subtitles step looks for a cue on screen. */
var CUE_POLL_MS = 500;
/** How many times it looks before giving up, so roughly ten seconds. */
var CUE_POLL_ATTEMPTS = 20;
/** Length of the bandwidth measurement window. */
var MEASURE_SECONDS = 20;
/**
 * Seconds of wall clock per second the player spent inside segment requests.
 * Below 1x it is downloading for longer than the media it collects lasts,
 * which no buffer can absorb forever; above it there is margin, however small
 * it looks.
 */
var HEADROOM_SUSTAIN = 1;
/** Headroom under which the margin is worth a mention, but only alongside real symptoms. */
var HEADROOM_TIGHT = 1.2;
/** Share of dropped frames above which playback is not considered clean. */
var DROPPED_LIMIT = 0.005;
/** How often the coachmark checks whether the start button has settled. */
var TOUR_SAMPLE_MS = 60;
/** Consecutive still samples required before it measures. */
var TOUR_STABLE_SAMPLES = 3;
/** Card width, so the coachmark fits the panel column instead of sprawling across the player. */
var TOUR_WIDTH = 300;
/** Pause on the green confirmation before moving to the next step. */
var ADVANCE_DELAY_MS = 1400;
/** Steps that measure on their own rather than waiting for the user to act. */
var MEASURED_STEPS = ["bandwidth", "stability"];
function trackLabel(track) {
    if (track.label && track.language)
        return "".concat(track.label, " (").concat(track.language, ")");
    return track.label || track.language || "untitled";
}
function formatRate(bitsPerSecond) {
    if (bitsPerSecond >= 1000000)
        return "".concat((bitsPerSecond / 1000000).toFixed(1), " Mbps");
    return "".concat((bitsPerSecond / 1000).toFixed(0), " kbps");
}
/**
 * Which audio track is on, keyed so the comparison survives a native track
 * list. See `trackKey`: Safari's lists routinely carry empty ids, and two of
 * those compare equal, which would hide the very switch this step tests.
 */
function enabledAudioKey(player) {
    var active = player.getAudioTracks().find(function (track) { return track.enabled; });
    return active ? trackKey(active) : undefined;
}
/** The subtitle track currently on screen, ignoring tracks no menu offers. */
function showingTextTrack(player) {
    return selectableTextTracks(player).find(function (track) { return track.mode === "showing"; });
}
/**
 * True once the active text track has at least one cue on screen. A caption
 * button that lights up while nothing is rendered (empty, 404 or CORS-blocked
 * VTT) is the failure this catches, and reading `mode` alone would miss it.
 */
function hasVisibleCue(container) {
    var _a, _b;
    var vjs = getUnderlyingPlayer(container);
    if (!vjs)
        return false;
    var tracks = vjs.textTracks();
    for (var i = 0; i < tracks.length; i++) {
        var track = tracks[i];
        if (track.mode === "showing" && ((_b = (_a = track.activeCues) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0)
            return true;
    }
    return false;
}
function baselineFrom(diagnostics, session) {
    var _a, _b;
    return {
        uptimeSeconds: session.uptimeSeconds,
        rebufferCount: session.rebufferCount,
        rebufferSeconds: session.rebufferSeconds,
        abrSwitches: session.abrSwitches,
        abrDownSwitches: session.abrDownSwitches,
        droppedFrames: (_a = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.droppedFrames) !== null && _a !== void 0 ? _a : 0,
        totalFrames: (_b = diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.totalFrames) !== null && _b !== void 0 ? _b : 0,
        stats: diagnostics === null || diagnostics === void 0 ? void 0 : diagnostics.stats
    };
}
/**
 * Picture of what the user is being asked to do, shown only while its step is
 * active. Until a real screenshot is set on the step, this renders a labelled
 * frame of the right shape, so the layout is already settled and adding the
 * images later changes nothing but the `screenshot` field.
 */
function StepPicture(_a) {
    var _b;
    var step = _a.step;
    var caption = "Where to click: ".concat((_b = step.shows) !== null && _b !== void 0 ? _b : step.title);
    if (step.screenshot) {
        return _jsx("img", { className: "player-diagnostics__step-shot", src: step.screenshot, alt: caption });
    }
    if (!step.illustration)
        return null;
    return _jsx(StepIllustration, { target: step.illustration, caption: caption });
}
/**
 * Verdict badge for the result card. A filled circle carrying a tick, a bang
 * or a cross, big enough to read as an outcome rather than as another step
 * marker in the list above it.
 */
var VerdictIcon = function (_a) {
    var verdict = _a.verdict;
    var fill = verdict === "pass" ? "#199e70" : verdict === "warn" ? "#d9822b" : "#d95926";
    return (_jsxs("svg", __assign({ className: "player-diagnostics__result-icon", viewBox: "0 0 24 24", "aria-hidden": "true" }, { children: [_jsx("circle", { cx: "12", cy: "12", r: "11", fill: fill }), verdict === "pass" && (_jsx("path", { d: "M7 12.5 l3.2 3.2 L17 9", stroke: "#fff", strokeWidth: "2.2", fill: "none", strokeLinecap: "round", strokeLinejoin: "round" })), verdict === "warn" && (_jsxs("g", __assign({ fill: "#fff" }, { children: [_jsx("rect", { x: "10.9", y: "6", width: "2.2", height: "8", rx: "1.1" }), _jsx("circle", { cx: "12", cy: "17", r: "1.4" })] }))), verdict === "fail" && (_jsxs("g", __assign({ stroke: "#fff", strokeWidth: "2.2", strokeLinecap: "round" }, { children: [_jsx("line", { x1: "8", y1: "8", x2: "16", y2: "16" }), _jsx("line", { x1: "16", y1: "8", x2: "8", y2: "16" })] })))] })));
};
var TrackWizard = function (_a) {
    var player = _a.player, containerRef = _a.containerRef, diagnostics = _a.diagnostics, session = _a.session, onResultsChange = _a.onResultsChange, buildReport = _a.buildReport, reportSubject = _a.reportSubject, supportEmail = _a.supportEmail, simulate = _a.simulate, _b = _a.interactive, interactive = _b === void 0 ? false : _b;
    var _c = useState(false), started = _c[0], setStarted = _c[1];
    // The panel opens with a coachmark on the start button. Testers land on this
    // page to run a check, and without it the panel reads as a wall of readouts
    // with no obvious way in. Dismissed for the rest of the visit once closed.
    // Anchored to the button element itself, so the spotlight measures exactly
    // what it highlights.
    var startButtonRef = useRef(null);
    var _d = useState(false), tourOpen = _d[0], setTourOpen = _d[1];
    // The ids to run on this pass, in STEPS order, and how far through them we
    // are. A queue rather than an index into STEPS so a re-run can cover only
    // the checks that did not pass, leaving the others' results standing.
    var _e = useState([]), queue = _e[0], setQueue = _e[1];
    var _f = useState(0), cursor = _f[0], setCursor = _f[1];
    var _g = useState({}), results = _g[0], setResults = _g[1];
    var _h = useState(null), countdown = _h[0], setCountdown = _h[1];
    // Set once the player has confirmed a step technically worked and we are
    // waiting on the tester to say whether it worked *for them*.
    var _j = useState(null), pending = _j[0], setPending = _j[1];
    var _k = useState(""), note = _k[0], setNote = _k[1];
    // Read by the step timeout, which must not declare a step missed while the
    // tester is busy answering its question.
    var awaitingAnswer = useRef(false);
    // Bumped by "Retry" to re-run the active step's effect with a fresh baseline.
    var _l = useState(0), attempt = _l[0], setAttempt = _l[1];
    // True once the stability step has had to open a measurement window of its
    // own, which changes what it asks of the tester while it runs.
    var _m = useState(false), ownStabilityWindow = _m[0], setOwnStabilityWindow = _m[1];
    // Latest telemetry, reachable from the step effect without listing it as a
    // dependency: the samples arrive every second and would otherwise restart
    // the active step on every tick.
    var latest = useRef({ diagnostics: diagnostics, session: session });
    latest.current = { diagnostics: diagnostics, session: session };
    // Counters as they were when the bandwidth measurement opened, so the
    // stability step can report on that same window instead of the whole session.
    var windowBaseline = useRef(null);
    useEffect(function () {
        onResultsChange === null || onResultsChange === void 0 ? void 0 : onResultsChange(results);
    }, [results, onResultsChange]);
    useEffect(function () {
        if (started)
            return undefined;
        // A fixed delay was not enough: the page keeps moving for as long as the
        // player is sizing itself, and how long that takes depends on the
        // source. Watch the button instead and open only once it has stopped
        // moving, since a spotlight measured against a moving target lands
        // where the button used to be.
        var timer = 0;
        var previousTop = null;
        var stableSamples = 0;
        var waitForStillness = function () {
            var _a, _b;
            var top = (_b = (_a = startButtonRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) !== null && _b !== void 0 ? _b : null;
            stableSamples = top !== null && top === previousTop ? stableSamples + 1 : 0;
            previousTop = top;
            if (stableSamples >= TOUR_STABLE_SAMPLES) {
                setTourOpen(true);
                return;
            }
            timer = window.setTimeout(waitForStillness, TOUR_SAMPLE_MS);
        };
        timer = window.setTimeout(waitForStillness, TOUR_SAMPLE_MS);
        return function () { return window.clearTimeout(timer); };
    }, [started]);
    // Pause between a confirmation and the next step, cancelled whenever the user
    // takes over (retry, skip, run again) so a pending hand-over cannot skip a step.
    var advanceTimer = useRef(undefined);
    var cancelAdvance = useCallback(function () {
        if (advanceTimer.current !== undefined)
            window.clearTimeout(advanceTimer.current);
        advanceTimer.current = undefined;
    }, []);
    useEffect(function () { return cancelAdvance; }, [cancelAdvance]);
    var finish = useCallback(function (id, result, advance) {
        setResults(function (current) {
            var _a;
            return (__assign(__assign({}, current), (_a = {}, _a[id] = result, _a)));
        });
        if (!advance)
            return;
        cancelAdvance();
        advanceTimer.current = window.setTimeout(function () {
            advanceTimer.current = undefined;
            setCursor(function (index) { return index + 1; });
        }, ADVANCE_DELAY_MS);
    }, [cancelAdvance]);
    var activeStepId = started ? queue[cursor] : undefined;
    // Keeps the latest `finish` reachable from the subscription effect below without
    // listing it as a dependency, so a re-render cannot re-subscribe mid-step.
    var finishRef = useRef(finish);
    finishRef.current = finish;
    useEffect(function () {
        if (!player || !activeStepId)
            return;
        var definitionForStep = STEPS.find(function (step) { return step.id === activeStepId; });
        var complete = function (result) {
            var _a;
            // The event only proves the player did something. When interactive mode
            // is on, the step is not done until the tester says they heard or saw
            // it, so park here and let the answer finish it.
            if (interactive && definitionForStep.confirm && result.state === "done") {
                awaitingAnswer.current = true;
                setPending({ id: activeStepId, detail: (_a = result.detail) !== null && _a !== void 0 ? _a : "" });
                return;
            }
            finishRef.current(activeStepId, result, true);
        };
        // Skip what this source cannot do: an unavailable feature is a result of the
        // test, not a gap in it.
        var definition = STEPS.find(function (step) { return step.id === activeStepId; });
        var audioTracks = player.getAudioTracks();
        // Unselectable tracks are left out on purpose: Safari invents an unnamed
        // caption track for HLS streams that carry no subtitles, and the player
        // keeps it out of its own menu, so counting it would ask the tester to
        // switch on something they cannot reach.
        var textTracks = selectableTextTracks(player);
        var capabilities = detectCapabilities(latest.current.diagnostics, containerRef.current);
        var unsupported = (activeStepId === "audioLanguage" && audioTracks.length < 2) ||
            (activeStepId === "subtitlesOn" && textTracks.length === 0) ||
            (activeStepId === "subtitlesLanguage" && textTracks.length < 2) ||
            // Native playback (Safari, every iOS browser) reports no throughput,
            // so there is nothing to measure and no point holding the tester for
            // the full window first.
            (activeStepId === "bandwidth" && capabilities.known && !capabilities.delivery);
        if (unsupported) {
            finishRef.current(activeStepId, { state: "unavailable", detail: definition.unavailable }, true);
            return;
        }
        var baselineAudio = enabledAudioKey(player);
        var baselineText = showingTextTrack(player);
        var baselineTextKey = baselineText ? trackKey(baselineText) : undefined;
        var cueTimer;
        var measureTimer;
        var onVolumeChange = function (_a) {
            var volume = _a.volume, muted = _a.muted;
            if (muted || volume <= 0)
                return;
            complete({ state: "done", detail: "Unmuted at ".concat(Math.round(volume * 100), "% volume") });
        };
        var onAudioTrackChange = function () {
            var active = player.getAudioTracks().find(function (track) { return track.enabled; });
            if (!active || trackKey(active) === baselineAudio)
                return;
            complete({ state: "done", detail: "Audio switched to ".concat(trackLabel(active)) });
        };
        var onSubtitlesOn = function () {
            var active = showingTextTrack(player);
            if (!active)
                return;
            // The track is on. Now wait for a cue to prove something is really rendered.
            player.off("texttrackchange", onSubtitlesOn);
            var attemptsLeft = CUE_POLL_ATTEMPTS;
            var poll = function () {
                if (hasVisibleCue(containerRef.current)) {
                    complete({ state: "done", detail: "".concat(trackLabel(active), " on, cues rendering") });
                    return;
                }
                attemptsLeft -= 1;
                if (attemptsLeft <= 0) {
                    var seconds = (CUE_POLL_ATTEMPTS * CUE_POLL_MS) / 1000;
                    // Where the browser renders captions itself the page never sees
                    // a cue, however well subtitles are working, so silence here
                    // says nothing about the stream and must not read as a fault.
                    // The track did turn on, which is the part the page can vouch
                    // for; in interactive mode the tester answers for the rest.
                    if (!canInspectCues(containerRef.current)) {
                        complete({
                            state: "done",
                            detail: "".concat(trackLabel(active), " on (this browser renders captions itself, so the page cannot verify the cues)")
                        });
                        return;
                    }
                    complete({
                        state: "warn",
                        detail: "".concat(trackLabel(active), " is active but no cue appeared in ").concat(seconds, "s (empty or unreachable VTT?)")
                    });
                    return;
                }
                cueTimer = window.setTimeout(poll, CUE_POLL_MS);
            };
            poll();
        };
        var onSubtitlesLanguage = function () {
            var active = showingTextTrack(player);
            if (!active || trackKey(active) === baselineTextKey)
                return;
            complete({ state: "done", detail: "Subtitles switched to ".concat(trackLabel(active)) });
        };
        /**
         * Holds the stream untouched for a fixed window and reports what the line
         * did with it.
         *
         * The headline figure is the fetch rate: seconds of media pulled per second
         * spent inside segment requests, taken from VHS's cumulative transfer
         * counters. It needs no bitrate at all, which matters because the obvious
         * denominators are both wrong. The manifest's BANDWIDTH is the declared
         * PEAK, so comparing throughput against it invents a shortfall on streams
         * that are perfectly healthy, and `stats.bandwidth` is only the last
         * segment's round trip, dragged down by time-to-first-byte on short
         * segments.
         *
         * The verdict then rests on symptoms (a stall, a failed request, a step
         * down in quality), never on the size of a derived ratio alone.
         */
        var measureBandwidth = function () {
            windowBaseline.current = baselineFrom(latest.current.diagnostics, latest.current.session);
            var measured = [];
            var played = [];
            var remaining = MEASURE_SECONDS;
            // The wall-clock measure below only holds while playback is actually
            // running: a paused player fetches nothing and would look infinitely
            // healthy.
            var pausedDuringWindow = false;
            setCountdown(remaining);
            measureTimer = window.setInterval(function () {
                var _a, _b, _c, _d;
                if (player.paused)
                    pausedDuringWindow = true;
                var sample = (_a = latest.current.diagnostics) === null || _a === void 0 ? void 0 : _a.stats;
                if ((sample === null || sample === void 0 ? void 0 : sample.measuredBandwidth) && sample.peakBitrate) {
                    measured.push(sample.measuredBandwidth);
                    played.push(sample.peakBitrate);
                }
                remaining -= 1;
                setCountdown(remaining > 0 ? remaining : null);
                if (remaining > 0)
                    return;
                window.clearInterval(measureTimer);
                measureTimer = undefined;
                // What actually went wrong during the window, if anything.
                var baseline = windowBaseline.current;
                var current = latest.current;
                var stalls = current.session.rebufferCount - ((_b = baseline === null || baseline === void 0 ? void 0 : baseline.rebufferCount) !== null && _b !== void 0 ? _b : 0);
                var stepDowns = current.session.abrDownSwitches - ((_c = baseline === null || baseline === void 0 ? void 0 : baseline.abrDownSwitches) !== null && _c !== void 0 ? _c : 0);
                // The player's counters are cumulative for the source, so the window
                // is simply the difference between the two snapshots.
                var before = baseline === null || baseline === void 0 ? void 0 : baseline.stats;
                var after = (_d = current.diagnostics) === null || _d === void 0 ? void 0 : _d.stats;
                var deltaBytes = after && before ? after.bytesTransferred - before.bytesTransferred : 0;
                var deltaTransferMs = after && before ? after.transferDuration - before.transferDuration : 0;
                var deltaSeconds = after && before ? after.secondsLoaded - before.secondsLoaded : 0;
                var failedRequests = after && before ? after.requestsErrored - before.requestsErrored + (after.requestsTimedout - before.requestsTimedout) : 0;
                // Nothing measurable either way: this playback path reports no
                // delivery at all. Checked here rather than before the deltas,
                // because the counters stand on their own and a manifest that
                // declares no bitrate must not look like a missing engine.
                var usableCounters = deltaBytes > 0 && deltaTransferMs > 0 && deltaSeconds > 0;
                if (!usableCounters && measured.length === 0) {
                    complete({ state: "unavailable", detail: definition.unavailable });
                    return;
                }
                var figures;
                var headroom;
                if (usableCounters) {
                    // Judged on wall clock: of the MEASURE_SECONDS that passed, this
                    // is how long the player spent inside requests. Deliberately NOT
                    // derived from the engine's seconds-loaded counter, whose meaning
                    // shifts with how many loaders a source uses (demuxed audio adds
                    // a second one), so this number cannot be thrown off by it.
                    headroom = pausedDuringWindow ? undefined : MEASURE_SECONDS / (deltaTransferMs / 1000);
                    var throughput = (deltaBytes * 8) / (deltaTransferMs / 1000);
                    var contentBitrate = deltaSeconds > 0 ? (deltaBytes * 8) / deltaSeconds : undefined;
                    var margin = headroom !== undefined ? "".concat(headroom.toFixed(1), "x real time") : "playback was paused";
                    figures =
                        "".concat(contentBitrate !== undefined ? formatRate(contentBitrate) : "Content", " of content pulled at ") +
                            "".concat(formatRate(throughput), " (").concat(margin, ") over ").concat(MEASURE_SECONDS, "s");
                }
                else {
                    // No usable transfer counters: fall back to the last-segment estimate,
                    // reported as figures only since it is too coarse to judge on.
                    var averageMeasured = measured.reduce(function (total, value) { return total + value; }, 0) / measured.length;
                    var averagePlayed = played.reduce(function (total, value) { return total + value; }, 0) / played.length;
                    figures = "".concat(formatRate(averageMeasured), " downloaded against a declared ").concat(formatRate(averagePlayed), " over ").concat(MEASURE_SECONDS, "s");
                }
                if (stalls > 0) {
                    complete({ state: "failed", detail: "".concat(figures, ". Playback stalled ").concat(stalls, " time(s): the line did not keep up.") });
                }
                else if (failedRequests > 0) {
                    complete({ state: "failed", detail: "".concat(figures, ". ").concat(failedRequests, " segment request(s) failed or timed out.") });
                }
                else if (headroom !== undefined && headroom < HEADROOM_SUSTAIN) {
                    complete({
                        state: "warn",
                        detail: "".concat(figures, ". Segments arrived slower than real time; the buffer covered it, but there is no margin.")
                    });
                }
                else if (headroom !== undefined && headroom < HEADROOM_TIGHT && stepDowns > 0) {
                    complete({ state: "warn", detail: "".concat(figures, ". Quality stepped down ").concat(stepDowns, " time(s) on a thin margin.") });
                }
                else {
                    complete({
                        state: "done",
                        detail: "".concat(figures, ". No stalls, no failed requests and no quality drops: the line carries this stream.")
                    });
                }
            }, 1000);
        };
        /**
         * What playback did over a measured window: rebuffering, adaptive
         * switches and dropped frames, all as differences against the counters
         * taken when that window opened.
         */
        var summariseWindow = function (baseline, pausedDuringWindow, measuredSeconds) {
            var _a, _b, _c, _d;
            var current = latest.current;
            var rebufferCount = current.session.rebufferCount - baseline.rebufferCount;
            var rebufferSeconds = current.session.rebufferSeconds - baseline.rebufferSeconds;
            var abrSwitches = current.session.abrSwitches - baseline.abrSwitches;
            var abrDownSwitches = current.session.abrDownSwitches - baseline.abrDownSwitches;
            // A window this step held itself knows its own length; one opened by the
            // bandwidth step is measured off the playback clock, which also covers
            // the hand-over between the two.
            var windowSeconds = measuredSeconds !== null && measuredSeconds !== void 0 ? measuredSeconds : Math.max(1, current.session.uptimeSeconds - baseline.uptimeSeconds);
            var droppedDelta = ((_b = (_a = current.diagnostics) === null || _a === void 0 ? void 0 : _a.droppedFrames) !== null && _b !== void 0 ? _b : 0) - baseline.droppedFrames;
            var totalDelta = ((_d = (_c = current.diagnostics) === null || _c === void 0 ? void 0 : _c.totalFrames) !== null && _d !== void 0 ? _d : 0) - baseline.totalFrames;
            var droppedRatio = totalDelta > 0 ? droppedDelta / totalDelta : 0;
            // A browser that reports no renditions cannot report switches between
            // them either, and "none downward" out of that silence would read as a
            // clean adaptive run that was never observed at all.
            var abrPart = !capabilities.renditions
                ? "rendition changes not reported by this browser"
                : abrDownSwitches === 0
                    ? "".concat(abrSwitches, " rendition change(s), none downward")
                    : "".concat(abrDownSwitches, " step(s) down in quality");
            var parts = [
                rebufferCount === 0 ? "no rebuffering" : "".concat(rebufferCount, " rebuffer(s) for ").concat(rebufferSeconds.toFixed(1), "s"),
                abrPart,
                totalDelta > 0 ? "".concat((droppedRatio * 100).toFixed(2), "% frames dropped") : "frame stats unavailable"
            ];
            var detail = "Over ".concat(windowSeconds.toFixed(0), "s: ").concat(parts.join(", "), ".");
            // A player that was paused produced no frames and could not stall, so
            // a clean reading off that window would be a false pass.
            if (pausedDuringWindow) {
                complete({ state: "warn", detail: "".concat(detail, " Playback was paused during the window, so this says little: run it again.") });
                return;
            }
            if (rebufferCount === 0 && droppedRatio < DROPPED_LIMIT)
                complete({ state: "done", detail: detail });
            else
                complete({ state: "warn", detail: detail });
        };
        /**
         * Reports on the window the bandwidth step opened, or holds one of its own
         * when there was none.
         *
         * There is none whenever that step could not run: on Safari and every iOS
         * browser the engine reports no throughput, so it is marked unavailable
         * and never opens a window (a tester skipping it has the same effect).
         * Without one, every difference below would be taken against zero, which
         * means judging the whole session since playback began: the frames dropped
         * while the stream started up, and every stall the looping source caused
         * minutes ago, would all land on this step. That is why /videotest passed
         * Stability on Chrome and failed it on Safari on the same healthy line.
         */
        var reportStability = function () {
            var openedByBandwidth = windowBaseline.current;
            if (openedByBandwidth) {
                summariseWindow(openedByBandwidth, false);
                return;
            }
            // Its own window, kept local: nothing else may report on it, and a
            // later re-run has to open a fresh one rather than reuse this.
            var ownBaseline = baselineFrom(latest.current.diagnostics, latest.current.session);
            var pausedDuringWindow = false;
            var remaining = MEASURE_SECONDS;
            setOwnStabilityWindow(true);
            setCountdown(remaining);
            measureTimer = window.setInterval(function () {
                if (player.paused)
                    pausedDuringWindow = true;
                remaining -= 1;
                setCountdown(remaining > 0 ? remaining : null);
                if (remaining > 0)
                    return;
                window.clearInterval(measureTimer);
                measureTimer = undefined;
                summariseWindow(ownBaseline, pausedDuringWindow, MEASURE_SECONDS);
            }, 1000);
        };
        switch (activeStepId) {
            case "unmute":
                player.on("volumechange", onVolumeChange);
                break;
            case "audioLanguage":
                player.on("audiotrackchange", onAudioTrackChange);
                break;
            case "subtitlesOn":
                player.on("texttrackchange", onSubtitlesOn);
                break;
            case "subtitlesLanguage":
                player.on("texttrackchange", onSubtitlesLanguage);
                break;
            case "bandwidth":
                measureBandwidth();
                break;
            case "stability":
                reportStability();
                break;
        }
        // Only the user-driven steps can time out. The line checks finish on their
        // own clock, and a 25s limit would cut the measurement window short.
        var timeout = MEASURED_STEPS.includes(activeStepId)
            ? undefined
            : window.setTimeout(function () {
                // Reported, but left on screen with Retry/Skip rather than skipped:
                // the tester may simply have been looking elsewhere. Never while
                // they are answering the step's question.
                if (awaitingAnswer.current)
                    return;
                finishRef.current(activeStepId, { state: "missed", detail: "No change detected." }, false);
            }, STEP_TIMEOUT_MS);
        return function () {
            if (timeout !== undefined)
                window.clearTimeout(timeout);
            if (cueTimer !== undefined)
                window.clearTimeout(cueTimer);
            if (measureTimer !== undefined)
                window.clearInterval(measureTimer);
            setCountdown(null);
            player.off("volumechange", onVolumeChange);
            player.off("audiotrackchange", onAudioTrackChange);
            player.off("texttrackchange", onSubtitlesOn);
            player.off("texttrackchange", onSubtitlesLanguage);
        };
    }, [player, activeStepId, attempt, containerRef, interactive]);
    var answer = function (confirmed) {
        if (!pending)
            return;
        awaitingAnswer.current = false;
        var trimmed = note.trim();
        finish(pending.id, {
            // A tester saying the feature did not work for them is the most
            // direct evidence there is, so it fails the step outright.
            state: confirmed === "yes" ? "done" : "failed",
            detail: pending.detail,
            confirmed: confirmed,
            note: trimmed || undefined
        }, true);
        setPending(null);
        setNote("");
    };
    var clearPending = function () {
        awaitingAnswer.current = false;
        setPending(null);
        setNote("");
    };
    var retry = function () {
        if (!activeStepId)
            return;
        cancelAdvance();
        clearPending();
        setResults(function (current) {
            var next = __assign({}, current);
            delete next[activeStepId];
            return next;
        });
        setAttempt(function (value) { return value + 1; });
    };
    var skip = function () {
        if (!activeStepId)
            return;
        cancelAdvance();
        clearPending();
        setResults(function (current) {
            var _a;
            return (__assign(__assign({}, current), (_a = {}, _a[activeStepId] = { state: "missed", detail: "Skipped." }, _a)));
        });
        setCursor(function (index) { return index + 1; });
    };
    var start = function () {
        cancelAdvance();
        clearPending();
        windowBaseline.current = null;
        setOwnStabilityWindow(false);
        if (simulate) {
            // Jump straight to a finished run: empty queue means complete.
            setResults(SIMULATED_RESULTS[simulate]);
            setQueue([]);
            setCursor(0);
            setStarted(true);
            return;
        }
        setResults({});
        setQueue(STEPS.map(function (step) { return step.id; }));
        setCursor(0);
        setAttempt(function (value) { return value + 1; });
        setStarted(true);
    };
    /**
     * Re-runs only what did not pass. Confirmed checks and ones this stream does
     * not offer keep their results, so the summary stays complete instead of
     * being rebuilt from scratch.
     */
    var retryFailed = function () {
        var failed = STEPS.filter(function (step) {
            var _a;
            var state = (_a = results[step.id]) === null || _a === void 0 ? void 0 : _a.state;
            return state === "warn" || state === "failed" || state === "missed";
        });
        // A grouped step cannot be judged alone: the stability reading describes
        // the window the bandwidth measurement opens, so re-running one without
        // the other would report on a window that no longer exists.
        var groups = new Set(failed.map(function (step) { return step.group; }).filter(Boolean));
        var rerun = STEPS.filter(function (step) { return failed.includes(step) || (step.group !== undefined && groups.has(step.group)); });
        if (rerun.length === 0)
            return;
        cancelAdvance();
        clearPending();
        windowBaseline.current = null;
        setOwnStabilityWindow(false);
        setResults(function (current) {
            var next = __assign({}, current);
            for (var _i = 0, rerun_1 = rerun; _i < rerun_1.length; _i++) {
                var step = rerun_1[_i];
                delete next[step.id];
            }
            return next;
        });
        setQueue(rerun.map(function (step) { return step.id; }));
        setCursor(0);
        setAttempt(function (value) { return value + 1; });
    };
    var isComplete = started && cursor >= queue.length;
    var stateOf = function (id) {
        var result = results[id];
        if (result)
            return result.state;
        if (id === activeStepId)
            return "active";
        return "waiting";
    };
    var countOf = function () {
        var states = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            states[_i] = arguments[_i];
        }
        return STEPS.filter(function (step) { var _a; return states.includes((_a = results[step.id]) === null || _a === void 0 ? void 0 : _a.state); }).length;
    };
    var summary = {
        done: countOf("done"),
        warn: countOf("warn"),
        // A failed measurement and a check nobody performed both mean "not passed".
        notPassed: countOf("failed", "missed"),
        unavailable: countOf("unavailable")
    };
    // How many checks a re-run would cover, grouped steps pulled in, so the
    // button says exactly what it is about to do.
    var retryCount = (function () {
        var failed = STEPS.filter(function (step) {
            var _a;
            var state = (_a = results[step.id]) === null || _a === void 0 ? void 0 : _a.state;
            return state === "warn" || state === "failed" || state === "missed";
        });
        var groups = new Set(failed.map(function (step) { return step.group; }).filter(Boolean));
        return STEPS.filter(function (step) { return failed.includes(step) || (step.group !== undefined && groups.has(step.group)); }).length;
    })();
    var verdict = wizardVerdict(results);
    // Read at render time rather than kept in state: the panel re-renders every
    // telemetry tick anyway, and the step being described is the current one.
    var capabilities = detectCapabilities(diagnostics, containerRef.current);
    var instructionFor = function (step) {
        if (step.id === "stability" && ownStabilityWindow && step.instructionMeasuringAlone)
            return step.instructionMeasuringAlone;
        if (!capabilities.volume && step.instructionWithoutVolumeControl)
            return step.instructionWithoutVolumeControl;
        return step.instruction;
    };
    return (_jsxs("div", __assign({ className: "player-diagnostics__wizard" }, { children: [!started && (_jsxs("div", __assign({ className: "player-diagnostics__wizard-intro" }, { children: [_jsxs("p", { children: [STEPS.length, " guided checks: sound, audio languages and subtitles from the control bar, then a ", MEASURE_SECONDS, "-second measurement of the line."] }), interactive && (_jsx("p", __assign({ className: "player-diagnostics__interactive-note", "data-testid": "wizard-interactive" }, { children: "After each feature responds you will be asked whether it actually worked for you, with room for a note. Those answers go into the report." }))), simulate && (_jsxs("p", __assign({ className: "player-diagnostics__simulated", "data-testid": "wizard-simulated" }, { children: ["Simulation on (", simulate, "): the run is seeded with a canned outcome. Retrying runs those checks for real."] }))), _jsx(Button, __assign({ type: "primary", onClick: start, "data-testid": "wizard-start", ref: startButtonRef }, { children: "Start the checks" })), _jsx(Tour, { open: tourOpen && !started, onClose: function () { return setTourOpen(false); }, steps: [
                            {
                                title: "Start here",
                                description: "This panel checks the stream on your machine: sound, languages, subtitles and the connection. Press this button and it will tell you what to do, one step at a time.",
                                target: function () { return startButtonRef.current; },
                                // Anchored under the button and kept narrow, so the card
                                // stays in the panel column rather than spilling over the
                                // video next to it.
                                placement: "bottom",
                                style: { width: TOUR_WIDTH },
                                // The button is already in view. Letting the tour scroll to
                                // it moves the sticky panel under the mask that was just
                                // measured, which is what threw the spotlight off.
                                scrollIntoViewOptions: false,
                                nextButtonProps: { children: "Got it" }
                            }
                        ] })] }))), started && (_jsx("ol", __assign({ className: "player-diagnostics__steps" }, { children: STEPS.map(function (step) {
                    var state = stateOf(step.id);
                    var result = results[step.id];
                    var active = state === "active";
                    return (_jsxs("li", __assign({ className: "player-diagnostics__step player-diagnostics__step--".concat(state), "data-testid": "wizard-step-".concat(step.id) }, { children: [_jsx("span", { className: "player-diagnostics__step-marker", "aria-hidden": "true" }), _jsxs("div", __assign({ className: "player-diagnostics__step-body" }, { children: [_jsx("span", __assign({ className: "player-diagnostics__step-title" }, { children: step.title })), active && (_jsxs("span", __assign({ className: "player-diagnostics__step-counter" }, { children: ["Step ", cursor + 1, " of ", queue.length] }))), active && _jsx("p", __assign({ className: "player-diagnostics__step-instruction" }, { children: instructionFor(step) })), active && _jsx(StepPicture, { step: step }), active && step.note && _jsx("p", __assign({ className: "player-diagnostics__step-note" }, { children: step.note })), active && countdown !== null && (_jsxs("div", __assign({ className: "player-diagnostics__measuring", "data-testid": "measuring", role: "progressbar", "aria-label": "Measuring the line", "aria-valuemin": 0, "aria-valuemax": MEASURE_SECONDS, "aria-valuenow": MEASURE_SECONDS - countdown }, { children: [_jsxs("div", __assign({ className: "player-diagnostics__measuring-head" }, { children: [_jsx("span", __assign({ className: "player-diagnostics__measuring-label" }, { children: "Measuring, please wait" })), _jsxs("span", __assign({ className: "player-diagnostics__measuring-clock" }, { children: [countdown, "s"] }))] })), _jsx("div", __assign({ className: "player-diagnostics__measuring-track" }, { children: _jsx("div", { className: "player-diagnostics__measuring-fill", style: { width: "".concat(Math.round(((MEASURE_SECONDS - countdown) / MEASURE_SECONDS) * 100), "%") } }) }))] }))), (pending === null || pending === void 0 ? void 0 : pending.id) === step.id && (_jsxs("div", __assign({ className: "player-diagnostics__confirm", "data-testid": "confirm-".concat(step.id) }, { children: [_jsx("p", __assign({ className: "player-diagnostics__confirm-detail" }, { children: pending.detail })), _jsx("p", __assign({ className: "player-diagnostics__confirm-question" }, { children: step.confirm })), _jsx(Input.TextArea, { className: "player-diagnostics__confirm-note", value: note, onChange: function (event) { return setNote(event.target.value); }, placeholder: "Anything worth telling us (optional)", rows: 2, "data-testid": "confirm-note-".concat(step.id) }), _jsxs("div", __assign({ className: "player-diagnostics__confirm-actions" }, { children: [_jsx(Button, __assign({ size: "small", type: "primary", onClick: function () { return answer("yes"); }, "data-testid": "confirm-yes-".concat(step.id) }, { children: "Yes" })), _jsx(Button, __assign({ size: "small", danger: true, onClick: function () { return answer("no"); }, "data-testid": "confirm-no-".concat(step.id) }, { children: "No" }))] }))] }))), (result === null || result === void 0 ? void 0 : result.detail) && _jsx("p", __assign({ className: "player-diagnostics__step-detail" }, { children: result.detail })), (result === null || result === void 0 ? void 0 : result.confirmed) && (_jsxs("p", __assign({ className: "player-diagnostics__step-answer" }, { children: ["You answered ", _jsx("strong", { children: result.confirmed }), result.note ? ": ".concat(result.note) : ""] }))), state === "missed" && step.id === activeStepId && (_jsxs("div", __assign({ className: "player-diagnostics__step-actions" }, { children: [_jsx(Button, __assign({ size: "small", onClick: retry }, { children: "Retry" })), _jsx(Button, __assign({ size: "small", type: "text", onClick: skip }, { children: "Skip" }))] })))] }))] }), step.id));
                }) }))), isComplete && (_jsxs("div", __assign({ className: "player-diagnostics__result player-diagnostics__result--".concat(verdict), "data-testid": "wizard-summary" }, { children: [_jsxs("div", __assign({ className: "player-diagnostics__result-head" }, { children: [_jsx(VerdictIcon, { verdict: verdict }), _jsxs("div", __assign({ className: "player-diagnostics__result-heading" }, { children: [_jsx("span", __assign({ className: "player-diagnostics__result-eyebrow" }, { children: "Test complete" })), _jsx("p", __assign({ className: "player-diagnostics__verdict player-diagnostics__verdict--".concat(verdict), "data-testid": "wizard-verdict" }, { children: VERDICT_LABEL[verdict] }))] }))] })), _jsxs("ul", __assign({ className: "player-diagnostics__result-stats" }, { children: [_jsxs("li", __assign({ className: "player-diagnostics__result-stat player-diagnostics__result-stat--ok" }, { children: [summary.done, " confirmed"] })), verdict !== "pass" && summary.warn > 0 && (_jsxs("li", __assign({ className: "player-diagnostics__result-stat player-diagnostics__result-stat--warn" }, { children: [summary.warn, " warning", summary.warn === 1 ? "" : "s"] }))), verdict !== "pass" && summary.notPassed > 0 && (_jsxs("li", __assign({ className: "player-diagnostics__result-stat player-diagnostics__result-stat--fail" }, { children: [summary.notPassed, " not passed"] }))), verdict !== "pass" && summary.unavailable > 0 && (_jsxs("li", __assign({ className: "player-diagnostics__result-stat" }, { children: [summary.unavailable, " not offered"] })))] })), verdict !== "pass" && (_jsxs(_Fragment, { children: [_jsx("div", __assign({ className: "player-diagnostics__wizard-actions" }, { children: _jsxs(Button, __assign({ size: "small", onClick: retryFailed, "data-testid": "wizard-retry-failed" }, { children: ["Retry the ", retryCount, " check", retryCount === 1 ? "" : "s", " that did not pass"] })) })), buildReport && (_jsx(CopyReportButton, { buildReport: buildReport, subject: reportSubject !== null && reportSubject !== void 0 ? reportSubject : "Player test report", supportEmail: supportEmail }))] }))] })))] })));
};
export default TrackWizard;
